"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

import SortProducts, { SortOptions } from "./sort-products"

type Tag = { id: string; value: string }
type Subcategory = { id: string; name: string }

type RefinementListProps = {
  sortBy: SortOptions
  q?: string
  minPrice?: string
  maxPrice?: string
  inStock?: boolean
  selectedTagIds?: string[]
  availableTags?: Tag[]
  subcategories?: Subcategory[]
  selectedSubcategoryIds?: string[]
  "data-testid"?: string
}

const RefinementList = ({
  sortBy,
  q = "",
  minPrice = "",
  maxPrice = "",
  inStock = false,
  selectedTagIds = [],
  availableTags = [],
  subcategories = [],
  selectedSubcategoryIds = [],
  "data-testid": dataTestId,
}: RefinementListProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Local state for inputs that submit on apply/enter rather than on every keystroke
  const [searchTerm, setSearchTerm] = useState(q)
  const [minPriceInput, setMinPriceInput] = useState(minPrice)
  const [maxPriceInput, setMaxPriceInput] = useState(maxPrice)

  // Keep local state in sync if URL params change externally
  useEffect(() => setSearchTerm(q), [q])
  useEffect(() => setMinPriceInput(minPrice), [minPrice])
  useEffect(() => setMaxPriceInput(maxPrice), [maxPrice])

  const pushParams = useCallback(
    (updates: Record<string, string | string[] | null>) => {
      const params = new URLSearchParams(searchParams)
      // Filter changes reset pagination
      params.delete("page")

      for (const [key, value] of Object.entries(updates)) {
        params.delete(key)
        if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
          continue
        }
        if (Array.isArray(value)) {
          for (const v of value) params.append(key, v)
        } else {
          params.set(key, value)
        }
      }

      const qs = params.toString()
      router.push(qs ? `${pathname}?${qs}` : pathname)
    },
    [pathname, router, searchParams]
  )

  const setQueryParams = (name: string, value: string) => {
    pushParams({ [name]: value })
  }

  const submitSearch = () => {
    pushParams({ q: searchTerm.trim() || null })
  }

  const applyPriceRange = () => {
    pushParams({
      minPrice: minPriceInput.trim() || null,
      maxPrice: maxPriceInput.trim() || null,
    })
  }

  const toggleInStock = (checked: boolean) => {
    pushParams({ inStock: checked ? "1" : null })
  }

  const toggleTag = (tagId: string, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...selectedTagIds, tagId]))
      : selectedTagIds.filter((id) => id !== tagId)
    pushParams({ tags: next.length > 0 ? next : null })
  }

  const toggleSubcategory = (subcatId: string, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...selectedSubcategoryIds, subcatId]))
      : selectedSubcategoryIds.filter((id) => id !== subcatId)
    pushParams({ subcats: next.length > 0 ? next : null })
  }

  const hasActiveFilters =
    Boolean(q) ||
    Boolean(minPrice) ||
    Boolean(maxPrice) ||
    Boolean(inStock) ||
    selectedTagIds.length > 0 ||
    selectedSubcategoryIds.length > 0

  const clearAll = () => {
    pushParams({
      q: null,
      minPrice: null,
      maxPrice: null,
      inStock: null,
      tags: null,
      subcats: null,
    })
  }

  return (
    <div
      className="flex flex-col gap-8 py-4 mb-8 bg-white border border-aov-border rounded-[12px] p-5"
      data-testid={dataTestId}
    >
      {/* Search */}
      <div className="flex flex-col gap-2">
        <label
          htmlFor="refinement-search"
          className="font-display text-sm font-bold text-aov-navy uppercase tracking-wide"
        >
          Search
        </label>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submitSearch()
          }}
          className="flex gap-2"
        >
          <input
            id="refinement-search"
            type="search"
            placeholder="Search products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onBlur={submitSearch}
            className="flex-1 min-w-0 rounded-md border border-aov-border bg-white px-3 py-2 text-sm font-body text-aov-navy placeholder:text-[#9aa6bf] focus:outline-none focus:border-aov-orange"
            data-testid="search-input"
          />
          <button
            type="submit"
            className="px-3 py-2 rounded-md bg-aov-navy text-white text-sm font-body font-semibold hover:bg-[#0f1d36] transition-colors"
            aria-label="Apply search"
          >
            Go
          </button>
        </form>
      </div>

      {/* Price range */}
      <div className="flex flex-col gap-2">
        <span className="font-display text-sm font-bold text-aov-navy uppercase tracking-wide">
          Price Range
        </span>
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            min={0}
            placeholder="Min"
            value={minPriceInput}
            onChange={(e) => setMinPriceInput(e.target.value)}
            onBlur={applyPriceRange}
            onKeyDown={(e) => e.key === "Enter" && applyPriceRange()}
            className="w-full min-w-0 rounded-md border border-aov-border bg-white px-3 py-2 text-sm font-body text-aov-navy placeholder:text-[#9aa6bf] focus:outline-none focus:border-aov-orange"
            data-testid="min-price-input"
            aria-label="Minimum price"
          />
          <span className="text-[#6b7a99] text-sm" aria-hidden="true">
            –
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            placeholder="Max"
            value={maxPriceInput}
            onChange={(e) => setMaxPriceInput(e.target.value)}
            onBlur={applyPriceRange}
            onKeyDown={(e) => e.key === "Enter" && applyPriceRange()}
            className="w-full min-w-0 rounded-md border border-aov-border bg-white px-3 py-2 text-sm font-body text-aov-navy placeholder:text-[#9aa6bf] focus:outline-none focus:border-aov-orange"
            data-testid="max-price-input"
            aria-label="Maximum price"
          />
        </div>
      </div>

      {/* In stock only */}
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={inStock}
          onChange={(e) => toggleInStock(e.target.checked)}
          className="h-4 w-4 rounded border-aov-border accent-aov-orange"
          data-testid="in-stock-checkbox"
        />
        <span className="text-sm font-body font-medium text-aov-navy">
          In stock only
        </span>
      </label>

      {/* Subcategories */}
      {subcategories.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="font-display text-sm font-bold text-aov-navy uppercase tracking-wide">
            Sub-categories
          </span>
          <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
            {subcategories.map((s) => {
              const checked = selectedSubcategoryIds.includes(s.id)
              return (
                <label
                  key={s.id}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggleSubcategory(s.id, e.target.checked)}
                    className="h-4 w-4 rounded border-aov-border accent-aov-orange"
                  />
                  <span className="text-sm font-body text-aov-navy">
                    {s.name}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      )}

      {/* Tags */}
      {availableTags.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="font-display text-sm font-bold text-aov-navy uppercase tracking-wide">
            Tags
          </span>
          <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
            {availableTags.map((tag) => {
              const checked = selectedTagIds.includes(tag.id)
              return (
                <label
                  key={tag.id}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggleTag(tag.id, e.target.checked)}
                    className="h-4 w-4 rounded border-aov-border accent-aov-orange"
                  />
                  <span className="text-sm font-body text-aov-navy">
                    {tag.value}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      )}

      {/* Sort */}
      <SortProducts sortBy={sortBy} setQueryParams={setQueryParams} data-testid={dataTestId} />

      {/* Clear filters */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={clearAll}
          className="text-sm font-body font-semibold text-aov-orange hover:underline self-start"
          data-testid="clear-filters"
        >
          Clear all filters
        </button>
      )}
    </div>
  )
}

export default RefinementList
